import React, { useEffect, useRef, useState } from "react";
import clsx from "clsx";

import Fade from "@material-ui/core/Fade";
import MuiPopover from "@material-ui/core/Popover";

import { useWindowSize } from "hooks/screen";
import { HorizontalPosition, VerticalPosition, invertPosition } from "./helper";

import useStyles, { arrowSize, maxAnchorTop } from "./styles";

export interface Props {
  className?: string;
  onClose: () => void;
  anchorEl?: HTMLElement | null;
  onOpen?: () => void;
  children?: React.ReactNode;
  open?: boolean;
}

const Popover = (props: Props) => {
  const { anchorEl, onClose, onOpen, children } = props;

  const classes = useStyles();
  const { width, height } = useWindowSize();
  const arrowRef = useRef<HTMLDivElement>();
  const [arrowLeft, setArrowLeft] = useState("0");
  const [arrowTop, setArrowTop] = useState("0");
  const [popoverEl, setPopoverEl] = useState(null);
  const [popoverVerticalPosition, setPopoverVerticalPosition] =
    useState<VerticalPosition>(VerticalPosition.BOTTOM);
  const [popoverHorizontalPosition, setPopoverHorizontalPosition] =
    useState<HorizontalPosition>(HorizontalPosition.CENTER);

  const handleClose = () => {
    onClose();
  };

  const handleEnter = popover => {
    onOpen?.();
    setPopoverEl(popover);
  };

  const handleEntering = () => {
    const popoverRect = popoverEl.getBoundingClientRect();
    const anchorRect = anchorEl.getBoundingClientRect();
    const arrowRect = arrowRef.current.getBoundingClientRect();

    const popoverVerticalRoom =
      anchorRect.top + anchorRect.height + popoverRect.height;
    const popoverVerticalCenter =
      anchorRect.top +
      anchorRect.height / 2 -
      popoverRect.top -
      arrowRect.height / 2;
    const popoverHorizontalRoom =
      anchorRect.left + anchorRect.width + popoverRect.width;
    const popoverHorizontalCenter =
      anchorRect.left +
      anchorRect.width / 2 -
      popoverRect.left -
      arrowRect.width / 2;

    const popoverVerticalPos =
      popoverVerticalRoom < height
        ? VerticalPosition.BOTTOM
        : anchorRect.top > popoverRect.height
        ? VerticalPosition.TOP
        : VerticalPosition.CENTER;
    setPopoverVerticalPosition(popoverVerticalPos);

    const popoverHorizontalPos =
      popoverVerticalPos === VerticalPosition.CENTER
        ? popoverHorizontalRoom < width
          ? HorizontalPosition.RIGHT
          : HorizontalPosition.LEFT
        : HorizontalPosition.CENTER;
    setPopoverHorizontalPosition(popoverHorizontalPos);

    const arrowLeft =
      popoverHorizontalPos === HorizontalPosition.CENTER
        ? popoverHorizontalCenter
        : popoverHorizontalPos === HorizontalPosition.LEFT
        ? null
        : -arrowSize;
    setArrowLeft(arrowLeft ? `${arrowLeft}px` : "auto");

    const arrowTop =
      popoverVerticalPos === VerticalPosition.CENTER
        ? popoverVerticalCenter
        : popoverVerticalPos === VerticalPosition.TOP
        ? null
        : -arrowSize;
    setArrowTop(arrowTop ? `${arrowTop}px` : "auto");
  };

  const handleExited = () => {
    return;
  };

  useEffect(() => {
    if (popoverEl && anchorEl) {
      handleEntering();
    }
  }, [width, height]);

  return (
    <MuiPopover
      closeAfterTransition
      TransitionComponent={Fade}
      id="customized-popover"
      className={clsx(classes.popover, {
        [classes.popoverBottom]:
          popoverVerticalPosition === VerticalPosition.BOTTOM,
        [classes.popoverTop]: popoverVerticalPosition === VerticalPosition.TOP,
        [classes.popoverLeft]:
          popoverHorizontalPosition === HorizontalPosition.LEFT,
        [classes.popoverRight]:
          popoverHorizontalPosition === HorizontalPosition.RIGHT
      })}
      anchorEl={anchorEl}
      keepMounted
      open={Boolean(anchorEl)}
      onClose={handleClose}
      TransitionProps={{
        onExited: handleExited,
        onEnter: handleEnter,
        onEntering: handleEntering
      }}
      elevation={24}
      getContentAnchorEl={null}
      anchorOrigin={{
        vertical: popoverVerticalPosition,
        horizontal: popoverHorizontalPosition
      }}
      transformOrigin={{
        vertical:
          popoverVerticalPosition !== VerticalPosition.CENTER
            ? invertPosition(popoverVerticalPosition)
            : maxAnchorTop,
        horizontal: invertPosition(popoverHorizontalPosition)
      }}
    >
      <div
        ref={arrowRef}
        className={clsx(classes.arrow, {
          [classes.arrowTop]:
            popoverVerticalPosition === VerticalPosition.BOTTOM,
          [classes.arrowBottom]:
            popoverVerticalPosition === VerticalPosition.TOP,
          [classes.arrowLeft]:
            popoverHorizontalPosition === HorizontalPosition.RIGHT,
          [classes.arrowRight]:
            popoverHorizontalPosition === HorizontalPosition.LEFT
        })}
        style={{
          left: arrowLeft,
          top: arrowTop
        }}
      />

      <div className={classes.popoverContent}>{children}</div>
    </MuiPopover>
  );
};

export default Popover;
